import {useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {
  Box, Button, Checkbox, IconButton, Link as MuiLink, Typography,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {debounce} from 'lodash-es'
import {
  FC,
  Fragment,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useGetIdentity, useNotify, useRedirect, useTranslate} from 'react-admin'
import {Link, Navigate, useLocation, useParams} from 'react-router-dom'

import {Chat, ChatContextProvider, ChatList, ChatPage} from '../../components/chat'
import ChatFilterTabs from '../../components/chat/ChatFilterTabs'
import ChatHeader from '../../components/chat/ChatHeader'
import ChatMessageFileAttachmentListSidebar
  from '../../components/chat/ChatMessageFileAttachmentListSidebar'
import ChatMessageSearchInboxSidebar, {
  ChatMessageSearchInboxSidebarRef,
} from '../../components/chat/ChatMessageSearchInboxSidebar'
import ChatSearchInboxInput from '../../components/chat/ChatSearchInboxInput'
import SearchInboxChatMessageList from '../../components/chat/SearchInboxChatMessageList'
import {
  ArchiveIcon, CloseIcon, SearchIcon, SettingsIcon, UnarchiveIcon,
} from '../../components/icons'
import WhatsappAccountDisconnectionAlert
  from '../../components/WhatsappAccountDisconnectionAlert'
import WordsHighlighter from '../../components/WordsHighlighter'
import {SandboxContext} from '../../contexts/SandboxContext'
import useChatsQueryAndSubscription from '../../hooks/chat/useChatsQueryAndSubscription'
import useHasUnreadChatMessages from '../../hooks/chat/useHasUnreadChatMessages'
import useIsShowingTranslations from '../../hooks/chat/useIsShowingTranslations'
import useIsDesktop from '../../hooks/useIsDesktop'
import useMostRecentInboundChatMessageElapsedIntervals
  from '../../hooks/useMostRecentInboundChatMessageElapsedIntervals'
import useSearchParams from '../../hooks/useSearchParams'
import useTracker from '../../hooks/useTracker'
import {useUserDisplayName} from '../../hooks/useUserDisplayName'
import {
  Chats,
  ChatTypesEnum,
  CustomerUsers,
  MutationRoot,
  QueryRoot,
  Users,
} from '../../types/graphqlSchema'
import {REPLY_EXPIRATION_INTERVAL} from '../../utils/consts'
import formatPhoneNumber from '../../utils/phoneNumber'
import {isNativeMobileApp} from '../../utils/platform'
import {
  BulkSelectionContext, BulkSelectionContextProvider,
} from './BulkSelectionContext'
import ChannelSettingsDialog from './ChannelSettingsDialog'
import ChatAssignment from './ChatAssignment'
import ChatContextMenuButton from './ChatContextMenuButton'
import StartNewChatButton from './StartNewChatButton'

const ExternalChatPage = () => (
  <BulkSelectionContextProvider>
    <ExternalChatPageContent />
  </BulkSelectionContextProvider>
)

const ExternalChatPageContent = () => {
  const styles = useStyles()
  const translate = useTranslate()
  const {trackEvent} = useTracker()
  const {searchParams} = useSearchParams()
  const {channelId, chatId} = useParams<{channelId?: string, chatId?: string}>()
  const chatMessageId = searchParams.chatMessageId || undefined
  const isDesktop = useIsDesktop()
  const {identity: {merchantUserId: sessionMerchantUserId} = {}} = useGetIdentity()
  const {data: {channels_by_pk: channel} = {}} = useQuery<QueryRoot['channels_by_pk']>(
    CHANNEL_QUERY, {skip: !channelId, variables: {id: channelId}}
  )
  const {
    data: {channels: [firstChannel]} = {channels: []},
  } = useQuery<QueryRoot['channels']>(
    FIRST_CHANNEL_QUERY,
    {
      skip: !!channelId || !sessionMerchantUserId,
      variables: {merchantUserId: sessionMerchantUserId},
    }
  )
  const [
    {[chatId as string]: mostRecentInboundChatMessageElapsedInterval = null},
    {refresh: refreshMostRecentInboundChatMessageElapsedInterval},
  ] = useMostRecentInboundChatMessageElapsedIntervals({chatId})
  const isReplyWindowExpired = useMemo(
    () => (mostRecentInboundChatMessageElapsedInterval === null) ||
      (mostRecentInboundChatMessageElapsedInterval > REPLY_EXPIRATION_INTERVAL),
    [mostRecentInboundChatMessageElapsedInterval]
  )
  const hashValue = useLocation().hash.replace('#', '')
  const isMultiMerchantUserChannel = channel &&
    ((channel.channelMerchantUsers_aggregate?.aggregate?.count ?? 0) > 1)
  const chatFilters = isMultiMerchantUserChannel ?
    MULTI_USER_CHAT_FILTERS : SINGLE_USER_CHAT_FILTERS
  const chatFilter = chatFilters.find(f => f === hashValue) ?? chatFilters[0]
  const {
    data: {
      // @ts-ignore: Property 'firstMineChat' does not exist on type 'QueryResultWrapper
      firstMineChat: [firstMineChat], firstUnassignedChat: [firstUnassignedChat],
    } = {firstMineChat: [], firstUnassignedChat: []},
  } = useQuery<{data: {firstMineChat: Chats[], firstUnassignedChat: Chats[]}}>(
    FIRST_MINE_AND_FIRST_UNASSIGNED_CHAT_QUERY,
    {
      skip:
        !!chatId || !channelId || !sessionMerchantUserId || !isMultiMerchantUserChannel,
      variables: {channelId, sessionMerchantUserId},
    }
  )
  const [isSearchInboxVisible, setIsSearchInboxVisible] = useState(false)
  const chatsBoolExp = useMemo(() => (
    {
      isArchived: {_eq: false},
      ...({
        all: {},
        archived: {isArchived: {_eq: true}},
        mine: {assignedMerchantUserId: {_eq: sessionMerchantUserId}},
        unassigned: {assignedMerchantUserId: {_is_null: true}},
      }[chatFilter]),
    }
  ), [chatFilter, sessionMerchantUserId])
  const {
    chats,
    fetchEarlierChats,
    hasEarlierChats,
    isFetchingMore,
    loading,
    onResetFetchEarlierChatsOffset,
  } = useChatsQueryAndSubscription(
    chatsBoolExp,
    CHAT_FIELDS,
    chatFilter,
    {
      // We want to check for "isMultiMerchantUserChannel === undefined" to prevent
      // fetching "all" chats filter when the proper value of
      // isMultiMerchantUserChannel isn't known yet.
      skip: !channelId || !sessionMerchantUserId ||
        (isMultiMerchantUserChannel === undefined) || isSearchInboxVisible,
      variables: {
        where: {
          channelId: {_eq: channelId},
          isSpam: {_eq: false},
        },
      },
    },
  )
  useEffect(
    // When the user switch to another filter tab we reinitialize the chat load offset.
    // We also scroll back to the top of the chat list to fix the issue where the
    // "fetch more" function was immediately called on switch.
    onResetFetchEarlierChatsOffset, [chatFilter, onResetFetchEarlierChatsOffset]
  )
  const {data: {chats_by_pk: selectedChat} = {}} = useQuery<QueryRoot['chats_by_pk']>(
    CHAT_QUERY, {skip: !chatId, variables: {chatId}}
  )
  const [
    isChannelSettingsDialogOpen, setIsChannelSettingsDialogOpen,
  ] = useState(false)
  const [
    isShowingTranslations,
    setIsShowingTranslations,
  ] = useIsShowingTranslations(chatId)
  const {isSandbox, shouldOnboard} = useContext(SandboxContext)
  const chatSearchInboxInputRef = useRef<HTMLInputElement>()
  const chatMessageSearchInboxSidebarRef = useRef<ChatMessageSearchInboxSidebarRef>()
  const [searchInboxText, setSearchInboxText] = useState("")
  const [searchedChats, setSearchedChats] = useState<QueryRoot['chats']>([])
  const [
    searchedChatMessages,
    setSearchedChatMessages,
  ] = useState<QueryRoot['chat_messages']>([])
  const [
    searchChats,
    {loading: isSearchingChats},
  ] = useLazyQuery<QueryRoot['chats']>(CHATS_INBOX_SEARCH_QUERY)
  const [
    searchChatMessages,
    {loading: isSearchingChatMessages},
  ] = useLazyQuery<QueryRoot['chat_messages']>(CHAT_MESSAGES_INBOX_SEARCH_QUERY)
  const debounceSearchChatsAndChatMessages = useMemo(
    () => debounce((text: string) => {
      // Trigram index requires at least 3 characters to be used; we skip if the length
      // is less than that.
      if (text.trim().length < 3) return
      const variables = {
        channelId,
        pattern: `%${text.trim().split(' ').filter(Boolean).join('%')}%`,
      }
      trackEvent({
        action: 'SEARCH_CHATS',
        category: 'CHAT',
        source: 'CHAT_SEARCH_INBOX',
      })
      searchChats({variables})
        .then(({data}) => setSearchedChats(data?.chats ?? []))
      searchChatMessages({variables})
        .then(({data}) => setSearchedChatMessages(data?.chat_messages ?? []))
    }, 500), [channelId, searchChats, searchChatMessages, trackEvent])
  const [sidebarComponentType, setSidebarComponentType] = useState<SidebarComponentType>()
  useEffect(() => {!chatId && setSidebarComponentType(undefined)}, [chatId])
  const redirect = useRedirect()
  const {isSelectionMode} = useContext(BulkSelectionContext)
  const isSearching = isSearchingChats || isSearchingChatMessages
  useEffect(
    () => {
      setIsSearchInboxVisible(false)
      setSearchInboxText('')
      setSearchedChats([])
      setSearchedChatMessages([])
    },
    [channelId]
  )
  if (isSandbox && shouldOnboard) {
    return <Navigate to="/sandbox" />
  }
  if (!channelId && firstChannel) {
    return <Navigate to={`/inbox/${firstChannel.id}`} />
  }
  if (isDesktop && channelId && !chatId && (firstMineChat || firstUnassignedChat)) {
    // As no chat has been selected yet, we want to automatically redirect to the first
    // available chat.
    return <Navigate
      to={
        `/inbox/${channelId}/${(firstMineChat || firstUnassignedChat).id}#${
        firstMineChat ? 'mine' : 'unassigned'}`
      }
    />
  }
  if (
    isDesktop && channelId && !chatId && chats?.length &&
    (isMultiMerchantUserChannel === false)
  ) {
    return <Navigate to={`/inbox/${channelId}/${chats[0].id}#all`} />
  }
  return (
    <ChatPage
      chatListHeader={
        <>
          {channelId && (
            <WhatsappAccountDisconnectionAlert
              channelId={channelId}
              className={styles.whatsappAccountDisconnectionAlert}
            />
          )}
          {!isSearchInboxVisible && (
            <ChatListHeader
              channelId={channelId}
              chatFilter={chatFilter}
              chatFilters={chatFilters}
              isMultiMerchantUserChannel={!!isMultiMerchantUserChannel}
            />
          )}
          {!isSearchInboxVisible && isSelectionMode && <ChatListBulkActions />}
        </>
      }
      isSidebarOpen={!!sidebarComponentType}
      searchInbox={isSearchInboxVisible && (
        <ChatSearchInboxInput
          inputRef={chatSearchInboxInputRef}
          isShowingArrowLeftIcon
          onChange={v => {
            setSearchInboxText(v)
            debounceSearchChatsAndChatMessages(v)
          }}
          onLeftArrowIconClicked={() => {
            setIsSearchInboxVisible(false)
            setSearchInboxText("")
          }}
          placeholder={translate('searchInbox.searchInAllChatsPlaceholder')}
          value={searchInboxText}
        />
      )}
      sidebar={{
        CHAT_MEDIA: chatId && (
          <ChatMessageFileAttachmentListSidebar
            chatId={chatId}
            onClose={() => setSidebarComponentType(undefined)}
          />
        ),
        CHAT_MESSAGE_SEARCH_INBOX: chatId && (
          <ChatMessageSearchInboxSidebar
            chatId={chatId}
            onChatMessageClicked={() => !isDesktop && setSidebarComponentType(undefined)}
            onClose={() => setSidebarComponentType(undefined)}
            ref={chatMessageSearchInboxSidebarRef as MutableRefObject<any>}
          />
        ),
      }[sidebarComponentType as SidebarComponentType]}
      startNewChatButton={
        <StartNewChatButton channelId={channelId as string} disabled={!channelId} />
      }
      title={
        <Box alignItems="center" display="flex" justifyContent="space-between">
          {translate('chat.external.title')}
          <div>
            <Button
              className={styles.channelSettingsButton}
              color="primary"
              onClick={() => {
                setIsSearchInboxVisible(v => {
                  const isActivatingSearchInbox = !v
                  if (isActivatingSearchInbox) {
                    trackEvent({
                      action: 'OPEN_CHAT_SEARCH_INBOX',
                      category: 'CHAT',
                      source: 'CHAT_SEARCH_INBOX',
                    })
                    setTimeout(() => chatSearchInboxInputRef.current?.focus(), 0)
                  }
                  else {
                    setSearchInboxText("")
                  }
                  return isActivatingSearchInbox
                })
              }}
              variant={isSearchInboxVisible ? 'contained' : 'outlined'}
            >
              <SearchIcon />
            </Button>
            <Button
              className={styles.channelSettingsButton}
              color="primary"
              onClick={() => {
                setIsChannelSettingsDialogOpen(true)
              }}
              disabled={!channelId}
              variant="outlined"
            >
              <SettingsIcon />
            </Button>
          </div>
          <ChannelSettingsDialog
            channelId={channelId}
            onClose={() => setIsChannelSettingsDialogOpen(false)}
            open={isChannelSettingsDialogOpen}
          />
        </Box>
      }
    >
      <>
        {(!isSearchInboxVisible || (isSearchInboxVisible && searchInboxText)) && (
          <ChatList
            chatTitle={
              ({...props}) => (
                <ChatOwningUserDisplayName
                  {...props}
                  highlightWords={searchInboxText.trim()}
                />
              )
            }
            chats={isSearchInboxVisible ? searchedChats : (chats ?? [])}
            fetchMoreChats={fetchEarlierChats}
            hasMessagePreview
            hasMoreChats={hasEarlierChats}
            isChatSearchInbox={isSearchInboxVisible}
            isExternalMultiMerchantUserChat={!!isMultiMerchantUserChannel}
            isFetchingMore={isFetchingMore}
            leadingComponent={
              isSelectionMode ? ChatListLeadingSelectionCheckbox : undefined
            }
            listTitle={isSearchInboxVisible ?
              translate('searchInbox.chatsResultTitle').toUpperCase() :
              undefined
            }
            loading={(!isSearchInboxVisible && loading) || isSearching}
            rightComponent={({...props}) => (
              <ChatListRightComponent
                {...props}
                onOpenChatMessageFileAttachmentListSidebar={
                  chatId => {
                    setSidebarComponentType('CHAT_MEDIA')
                    redirect(`/inbox/${channelId}/${chatId}#${chatFilter}`)
                  }
                }
              />
            )}
            selectedChatId={chatId as string}
          />
        )}
        {isSearchInboxVisible && !isSearching && searchInboxText.length >= 3 && (
          <SearchInboxChatMessageList
            chatMessages={searchedChatMessages}
            chatTitle={({...props}) => (
              <ChatOwningUserDisplayName {...props} highlightWords="" />
            )}
            isShowingAssignedMerchantUser={!!isMultiMerchantUserChannel}
            listTitle={translate('searchInbox.messagesResultTitle').toUpperCase()}
            loading={false}
            searchWords={searchInboxText.trim()}
          />
        )}
      </>
      {!!chatId && (
        <ChatContextProvider {...{channelId, chatId, chatMessageId}}>
          <Chat
            header={
              <ChatHeader
                isSpam={selectedChat?.isSpam}
                redirectTo={
                  !!selectedChat?.owningUser?.customerUser?.merchantCustomerUsers.length ?
                    `/merchant_customer_users/${
                      selectedChat.owningUser.customerUser.merchantCustomerUsers[0].id}` :
                    undefined
                }
                subtitle={<ChatSubtitle owningUser={selectedChat?.owningUser} />}
                title={<ChatTitle owningUser={selectedChat?.owningUser} />}
              >
                <Button
                  className={styles.chatMessageSearchInboxButton}
                  onClick={() => {
                    setSidebarComponentType('CHAT_MESSAGE_SEARCH_INBOX')
                    setTimeout(
                      () => chatMessageSearchInboxSidebarRef.current
                        ?.focusSearchInboxInput(),
                      0,
                    )
                    trackEvent({
                      action: 'OPEN_SEARCH_INBOX',
                      category: 'CHAT',
                      source: 'CHAT_MESSAGE_LIST',
                    })
                  }}
                  size="small"
                  variant="text"
                >
                  <SearchIcon />
                </Button>
                {(isMultiMerchantUserChannel && selectedChat) && (
                  <ChatAssignment chat={selectedChat} className={styles.chatAssignment} />
                )}
                {selectedChat &&
                  <ChatContextMenuButton
                    chat={selectedChat}
                    isExternalMultiMerchantUserChat={!!isMultiMerchantUserChannel}
                    onOpenChatMessageFileAttachmentListSidebar={
                      () => setSidebarComponentType('CHAT_MEDIA')
                    }
                  />
                }
              </ChatHeader>
            }
            isReplyWindowExpired={isReplyWindowExpired}
            isShowingTranslations={isShowingTranslations}
            onChangeIsShowingTranslations={setIsShowingTranslations}
            onExternalInboundChatMessageReceived={
              refreshMostRecentInboundChatMessageElapsedInterval
            }
            type={ChatTypesEnum.External}
          />
        </ChatContextProvider>
      )}
    </ChatPage>
  )
}

const ChatListRightComponent: FC<ChatListRightComponentProps> = ({
  chat,
  isExternalMultiMerchantUserChat,
  onOpenChatMessageFileAttachmentListSidebar,
  ...props
}) => (
  <ChatContextMenuButton
    isExternalMultiMerchantUserChat={isExternalMultiMerchantUserChat}
    chat={chat}
    isInChatList
    onOpenChatMessageFileAttachmentListSidebar={
      () => onOpenChatMessageFileAttachmentListSidebar(chat.id)
    }
    {...props}
  />
)

const ChatListHeader: FC<ChatListHeaderProps> = ({
  channelId,
  chatFilter,
  chatFilters,
  isMultiMerchantUserChannel,
}) => {
  const styles = useStyles()
  const hasMineUnreadChatMessages = useHasUnreadChatMessages({
    channelId,
    chatType: ChatTypesEnum.External,
    isMineOnly: true,
    skip: !isMultiMerchantUserChannel,
  })
  const hasUnassignedUnreadChatMessages = useHasUnreadChatMessages({
    channelId,
    chatType: ChatTypesEnum.External,
    isUnassignedOnly: true,
    skip: !isMultiMerchantUserChannel,
  })
  const hasUnreadChatMessages = useHasUnreadChatMessages({
    channelId,
    chatType: ChatTypesEnum.External,
    skip: isMultiMerchantUserChannel,
  })
  return (
    <ChatFilterTabs
      chatType={ChatTypesEnum.External}
      className={styles.chatFilterTabs}
      filters={chatFilters.map(f => ({
        hasUnreadChatMessages: {
          'all': hasUnreadChatMessages,
          'mine': hasMineUnreadChatMessages,
          'unassigned': hasUnassignedUnreadChatMessages,
        }[f],
        name: f,
      }))}
      value={chatFilter}
    />
  )
}

const ChatListLeadingSelectionCheckbox: FC<{chat: Chats}> = ({chat}) => {
  const {onToggleChatSelected, selectedChats} = useContext(BulkSelectionContext)
  return (
    <Checkbox
      checked={selectedChats.some(({id}) => id === chat.id)}
      onChange={() => onToggleChatSelected(chat)}
      onClick={e => e.stopPropagation()}
      size="small"
    />
  )
}

const ChatListBulkActions = () => {
  const {onClearSelection, selectedChats} = useContext(BulkSelectionContext)
  const styles = useStyles()
  const translate = useTranslate()
  const notify = useNotify()
  const [bulkUpdate, {loading}] = useMutation<MutationRoot['update_chats']>(
    UPDATE_CHATS_MUTATION,
    {
      onCompleted: () => notify('chat.external.bulkActions.success'),
      onError: () => notify('ra.message.error', {type: 'error'}),
    }
  )
  const setArchived = (isArchived: boolean) => {
    bulkUpdate({
      variables: {
        chatIds: selectedChats.map(({id}) => id),
        setInput: {isArchived},
      },
    })
    onClearSelection()
  }
  return (
    <div className={styles.chatListBulkActions}>
      <IconButton onClick={onClearSelection} size="small">
        <CloseIcon color="inherit" />
      </IconButton>
      <Typography className={styles.chatListBulkActionsCount} variant="subtitle2">
        {translate('ra.action.bulk_actions', {smart_count: selectedChats.length})}
      </Typography>
      <div className={styles.chatListBulkActionsButtons}>
        {selectedChats.some(({isArchived}) => !isArchived) && (
          <Button
            color="secondary"
            disabled={loading}
            onClick={() => setArchived(true)}
            size="small"
            startIcon={<ArchiveIcon />}
            variant="outlined"
          >
            {translate('chat.external.bulkActions.archive')}
          </Button>
        )}
        {selectedChats.some(({isArchived}) => isArchived) && (
          <Button
            color="secondary"
            disabled={loading}
            onClick={() => setArchived(false)}
            size="small"
            startIcon={<UnarchiveIcon />}
            variant="outlined"
          >
            {translate('chat.external.bulkActions.unarchive')}
          </Button>
        )}
      </div>
    </div>
  )
}

const UPDATE_CHATS_MUTATION = gql`
  mutation($chatIds: [uuid!]!, $setInput: chats_set_input!) {
    update_chats(_set: $setInput, where: {id: {_in: $chatIds}}){
      returning{id isArchived}
    }
  }
`

const ChatOwningUserDisplayName: FC<ChatOwningUserDisplayNameProps> = ({
  chat, highlightWords,
}) => {
  const styles = useStyles()
  return (
    <Typography
      className={`
        ${styles.chatOwningUserDisplayName}
        ${chat.unreadMessageCount ? styles.unreadMessageItem : ''}
      `}
      color="textPrimary"
      variant="body1"
    >
      <ChatTitle highlightWords={highlightWords} owningUser={chat?.owningUser} />
    </Typography>
  )
}

const ChatTitle: FC<ChatTitleProps> = ({highlightWords, owningUser}) => {
  const styles = useStyles()
  const userDisplayName = useUserDisplayName()
  const hasMerchantCustomerUser =
    !!owningUser?.customerUser?.merchantCustomerUsers?.length
  const displayName = userDisplayName(owningUser)?.trim()
  return (
    <span className={`${styles.chatTitle} notranslate`}>
      {(hasMerchantCustomerUser && displayName) ? (
        <WordsHighlighter
          searchWords={highlightWords?.split(' ')}
          textToHighlight={displayName}
        />
      ) : (
        <>
          <WordsHighlighter
            searchWords={highlightWords?.split(' ')}
            textToHighlight={
              formatPhoneNumber(owningUser?.customerUser?.whatsappPhoneNumber) ?? ''
            }
          />
          <small>
            ~
            <WordsHighlighter
              searchWords={highlightWords?.split(' ')}
              textToHighlight={displayName ?? ''}
            />
          </small>
        </>
      )}
    </span>
  )
}

const ChatSubtitle = ({owningUser}) => {
  const hasMerchantCustomerUser = !!owningUser?.customerUser.merchantCustomerUsers.length
  return (
    hasMerchantCustomerUser ?
      <MerchantCustomerUserSubtitle value={owningUser?.customerUser} /> :
      <CustomerUserSubtitle value={owningUser?.customerUser} />
  )
}

const MerchantCustomerUserSubtitle: FC<MerchantCustomerUserSubtitleProps> = ({value}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const data = [
    value.merchantCustomerUsers[0].companyName?.trim(),
    value.merchantCustomerUsers[0].role?.trim(),
    value.merchantCustomerUsers[0].customerCode?.trim() &&
      `${
        translate('chat.external.header.merchantCustomerUserSubtitle.customerCodePrefix')
      } ${value.merchantCustomerUsers[0].customerCode}`,
    value.merchantCustomerUsers[0].companyCity?.trim(),
  ].filter(Boolean).filter((_, i) => i < 3)
  return (
    <div className={styles.merchantCustomerUserSubtitle}>
      {data.map((v, i) => (
        <Fragment key={i}>
          {i > 0 && <span className={styles.merchantCustomerUserSubtitleDivider}></span>}
          <span>{v}</span>
        </Fragment>
      ))}
    </div>
  )
}

const CustomerUserSubtitle = ({value}) => (
  <MuiLink
    className={useStyles().contactLink}
    component={Link}
    to={
      `/merchant_customer_users/create?whatsappPhoneNumber=${
        encodeURIComponent(value?.whatsappPhoneNumber ?? '')
      }&firstName=${encodeURIComponent(value?.whatsappDisplayName ?? '')}`
    }
  >
    {useTranslate()('chat.external.header.saveContact')}
  </MuiLink>
)

const EmbeddedExternalChatPage = ({channelId, chatId}) => {
  const translate = useTranslate()
  const styles = useStyles()
  const [
    {[chatId]: mostRecentInboundChatMessageElapsedInterval},
    {refresh: refreshMostRecentInboundChatMessageElapsedInterval},
  ] = useMostRecentInboundChatMessageElapsedIntervals({chatId})
  const {
    data: {chats_by_pk: chat} = {}, loading: isLoadingChats,
  } = useQuery<QueryRoot['chats_by_pk']>(
    CHAT_QUERY, {variables: {chatId}}
  )
  const {data: {channels_by_pk: channel} = {}} = useQuery<QueryRoot['channels_by_pk']>(
    CHANNEL_QUERY, {variables: {id: channelId}}
  )
  const isMultiMerchantUserChannel = (
    channel?.channelMerchantUsers_aggregate?.aggregate?.count ?? 0
  ) > 1
  const isReplyWindowExpired = useMemo(
    () => !mostRecentInboundChatMessageElapsedInterval ||
      (mostRecentInboundChatMessageElapsedInterval > REPLY_EXPIRATION_INTERVAL),
    [mostRecentInboundChatMessageElapsedInterval]
  )
  // TODO: `useCallback` has been used assuming that it's needed to stabilize the
  // reference in `openChatInNewWindow` and prevent rerenderings of the `Chat` component.
  // Tests are needed to validate if `useCallback` is really needed in order to prevent
  // such rerenderings
  const openChatInNewWindow = useCallback(() => {
    window.open(`/inbox/${channelId}/${chatId}`, '_blank')
  }, [channelId, chatId])
  const [
    isShowingTranslations,
    setIsShowingTranslations,
  ] = useIsShowingTranslations(chatId)
  if (isLoadingChats || !chat) return null
  return (
    <>
      <Button
        className={styles.openChatInNewWindowButton}
        color="primary"
        onClick={openChatInNewWindow}
        variant="contained"
      >
        {translate('pages.startExternalChat.embeddedExternalChatPage.openInFlinkit')}
      </Button>
      <ChatContextProvider {...{channelId, chatId}}>
        <Chat
          header={
            <ChatHeader
              redirectTo={
                !!chat?.owningUser?.customerUser?.merchantCustomerUsers.length ?
                `/merchant_customer_users/${
                  chat.owningUser.customerUser.merchantCustomerUsers[0].id}` :
                  undefined
              }
              subtitle={
                (
                  !!chat.owningUser?.customerUser?.merchantCustomerUsers.length
                ) && (<ChatSubtitle owningUser={chat.owningUser} />)
              }
              title={<ChatTitle owningUser={chat.owningUser} />}
            >
              {isMultiMerchantUserChannel && (
                <ChatAssignment chat={chat} className={styles.chatAssignment} />
              )}
            </ChatHeader>
          }
          isReplyWindowExpired={isReplyWindowExpired}
          isShowingTranslations={isShowingTranslations}
          onChangeIsShowingTranslations={setIsShowingTranslations}
          onExternalInboundChatMessageReceived={
            refreshMostRecentInboundChatMessageElapsedInterval
          }
          type={ChatTypesEnum.External}
        />
      </ChatContextProvider>
    </>
  )
}

const useStyles = makeStyles(theme => ({
  channelSettingsButton: {
    ...(isNativeMobileApp && {
      '&:hover': {
        '& .MuiSvgIcon-root': {
          color: theme.palette.info.main,
        },
      },
    }),
  },
  chatAssignment: {
    maxWidth: theme.typography.pxToRem(168),
  },
  chatFilterTabs: {
    marginTop: theme.remSpacing(1),
  },
  chatHeaderSettings: {
    borderTop: '0.025rem solid #CDD5DF',
    // TODO: Use the right components+props to achieve desired layouting, don't 100%
    width: '100%',
  },
  chatListBulkActions: {
    alignItems: 'center',
    background: theme.palette.background.paper,
    color: theme.palette.secondary.main,
    display: 'flex',
    // Nothing here may shrink or truncate: on a narrow chat list the buttons
    // wrap onto their own row instead, keeping every label readable.
    flexWrap: 'wrap',
    gap: theme.remSpacing(1),
    paddingBottom: theme.remSpacing(1),
    paddingLeft: theme.remSpacing(2),
    paddingRight: theme.remSpacing(2),
    paddingTop: theme.remSpacing(1),
  },
  chatListBulkActionsButtons: {
    display: 'flex',
    flexShrink: 0,
    gap: theme.remSpacing(1),
    marginLeft: 'auto',
  },
  chatListBulkActionsCount: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  chatMessageSearchInboxButton: {
    '& .MuiSvgIcon-root': {
      color: theme.palette.secondary.main,
    },
    borderRadius: '50px',
  },
  chatOwningUserDisplayName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatTitle: {
    '& small': {
      color: theme.palette.text.secondary,
      marginLeft: theme.remSpacing(1),
      whiteSpace: 'nowrap',
      ...theme.typography.body2,
    },
    display: 'inline',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  contactLink: {
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  logo: {
    '& svg': {
      height: theme.typography.pxToRem(32),
      width: theme.typography.pxToRem(32),
    },
    display: 'inline-block',
    transform: `translateY(${theme.remSpacing(-2)})`,
  },
  merchantCustomerUserSubtitle: {
    '& $merchantCustomerUserSubtitleDivider': {
      borderLeft: '1px solid #ccc',
      display: 'inline-block',
      height: '2.5ch',
      marginLeft: theme.remSpacing(1),
      marginRight: theme.remSpacing(1),
    },
    alignItems: 'center',
    display: 'flex',
    marginTop: theme.remSpacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  merchantCustomerUserSubtitleDivider: {},
  openChatInNewWindowButton: {
    left: theme.remSpacing(3),
    position: 'absolute',
    top: theme.remSpacing(11),
    zIndex: 1004,
  },
  subtitleTooltip: {
    maxWidth: theme.typography.pxToRem(250),
    padding: theme.remSpacing(1.5),
  },
  unreadMessageItem: {
    fontWeight: theme.typography.subtitle2.fontWeight,
  },
  whatsappAccountDisconnectionAlert: {
    marginLeft: theme.remSpacing(2),
    marginRight: theme.remSpacing(2),
  },
}))

const MULTI_USER_CHAT_FILTERS: ChatFilter[] = ['mine', 'unassigned', 'all', 'archived']
const SINGLE_USER_CHAT_FILTERS: ChatFilter[] = ['all', 'archived']

const CHANNEL_QUERY = gql`query($id: uuid!){
  channels_by_pk(id: $id){channelMerchantUsers_aggregate{aggregate{count}} id}
}`

const FIRST_CHANNEL_QUERY = gql`query($merchantUserId: uuid!){
  channels(
    limit: 1,
    order_by: {name: asc}
    where: {channelMerchantUsers: {merchantUserId: {_eq: $merchantUserId}}}
  ){id}
}`

const CHAT_FIELDS = `
  assignedMerchantUser{
    firstName
    id
    lastName
    userColor
  }
  channelId
  chatMessages(
    limit: 1
    order_by: {insertionTimestamp: desc}
    where: {whatsappMessagesStatus: {_neq: CANCELED}}
  ){
    authorUser{
      customerUser{id merchantCustomerUsers{firstName id lastName} whatsappDisplayName} id
    }
    chatMessageTemplate{id text}
    chatMessageTemplateValues{chatMessageTemplatePlaceholder{id index type} id value}
    id
    reactionEmoji
    reactedChatMessage{
      chatMessageTemplate{id text}
      chatMessageTemplateValues{id chatMessageTemplatePlaceholder{id index type} value}
      id
      text
    }
    text
    timestamp
  }
  hasUnreadFailedChatMessage
  id
  insertionTimestamp
  isArchived
  isSpam
  lastActivityTimestamp
  owningUser{
    customerUser{
      id
      merchantCustomerUsers{
        companyName companyCity customerCode firstName id lastName role
      }
      whatsappDisplayName
      whatsappPhoneNumber
    }
    id
  }
  type
  unreadMessageCount
  userChats{chatId isFlagged}
`

const CHAT_QUERY = gql`query($chatId: uuid!){
  chats_by_pk(id: $chatId){${CHAT_FIELDS}}
}`

const FIRST_MINE_AND_FIRST_UNASSIGNED_CHAT_QUERY = gql`
  query($channelId: uuid!, $sessionMerchantUserId: uuid!){
    firstMineChat: chats(
      limit: 1
      order_by: {lastActivityTimestamp: desc},
      where: {
        assignedMerchantUserId: {_eq: $sessionMerchantUserId}
        channelId: {_eq: $channelId}
        chatMessages_aggregate: {count: {predicate: {_gt: 0}}}
        isArchived: {_eq: false}
        isSpam: {_eq: false}
      }
    ){id chatMessages{id}}
    firstUnassignedChat: chats(
      limit: 1
      order_by: {lastActivityTimestamp: desc},
      where: {
        assignedMerchantUserId: {_is_null: true}
        channelId: {_eq: $channelId}
        chatMessages_aggregate: {count: {predicate: {_gt: 0}}}
        isArchived: {_eq: false}
        isSpam: {_eq: false}
      }
    ){id}
  }
`

const CHATS_INBOX_SEARCH_QUERY = gql`query($pattern: String!, $channelId: uuid!){
  chats(
    limit: 50
    order_by: {lastActivityTimestamp: desc}
    where: {
      channelId: {_eq: $channelId}
      owningUser: {
        customerUser: {merchantCustomerUsers: {fullText: {_ilike: $pattern}}}
      }
      isSpam: {_eq: false}
      type: {_eq: EXTERNAL}
    }
  ){${CHAT_FIELDS}}
}`

const CHAT_MESSAGES_INBOX_SEARCH_QUERY = gql`query($pattern: String!, $channelId: uuid!){
  chat_messages(
    limit: 50
    order_by: {insertionTimestamp: desc}
    where: {
      fullText: {_ilike: $pattern}
      chat: {
        channelId: {_eq: $channelId}
        isSpam: {_eq: false}
        type: {_eq: EXTERNAL}
      }
    }
  ){
    chatId
    chatMessageTemplate{id text}
    chatMessageTemplateValues{chatMessageTemplatePlaceholder{index type} value}
    id
    text
    timestamp
    chat{
      assignedMerchantUser{
        firstName
        id
        lastName
        userColor
      }
      channelId
      id
      insertionTimestamp
      isArchived
      isSpam
      owningUser{
        customerUser{
          id
          merchantCustomerUsers{
            companyName companyCity customerCode firstName id lastName role
          }
          whatsappDisplayName
          whatsappPhoneNumber
        }
        id
      }
      type
      userChats{chatId isFlagged}
    }
  }
}`

interface ChatListHeaderProps {
  channelId: string | undefined
  chatFilter: ChatFilter
  chatFilters: ChatFilter[]
  isMultiMerchantUserChannel: boolean
}

interface ChatOwningUserDisplayNameProps {
  chat: Chats
  highlightWords: string
}

interface ChatTitleProps {
  highlightWords?: string
  owningUser?: Users
}

interface MerchantCustomerUserSubtitleProps {
  value: CustomerUsers
}

interface ChatListRightComponentProps {
  chat: Chats
  isExternalMultiMerchantUserChat: boolean
  onOpenChatMessageFileAttachmentListSidebar: (chatId: string) => void
}

type SidebarComponentType = 'CHAT_MESSAGE_SEARCH_INBOX' | 'CHAT_MEDIA'

export {CHAT_FIELDS, ExternalChatPage as default, EmbeddedExternalChatPage}
